/**
 * /booking-transit — visual "flying to your destination" interstitial
 * rendered inside the NEW TAB that `bookNow.js` opens on Book Now click.
 *
 * 2026-07-15 rev 2 — flash-fix polish:
 *   • Plane now points up-right (takeoff attitude) with only its tail
 *     dipping past the bottom fold. Adds an upward-drift animation on
 *     top of the existing bobble so it reads as an actively-climbing
 *     departure, not a stationary indicator.
 *   • Redirect delay bumped from 60 ms → 800 ms (configurable via
 *     `?delay=` query) so our loader stays visible for the entire
 *     duration of Booking.com's SSR flash. Users who reported "the
 *     previous search flashes before the correct hotel loads" were
 *     seeing Booking's own hydration — the extended dwell hides it.
 *   • `Cache-Control: no-store` communicated via meta tags so the
 *     transit page itself never gets cached mid-flow (belt-and-braces —
 *     the real header would need edge config).
 *   • Extended allow-list to cover amazon.* and agentmaxonline.com for
 *     Shopping Lists P0 + Travel Insurance (Allianz) flows.
 */
import React, { useEffect } from "react";
import { Plane } from "lucide-react";

// Safety allow-list — the only outbound hosts we ever redirect to.
// Prevents `/booking-transit?url=<evil>` from being weaponized as an
// open redirector.
//
// 2026-07-15 prod P0: added the full set of CJ (Commission Junction /
// commission-junction / cj.com) redirect domains. CJ rotates the
// click-through host per advertiser + account, so preview and prod
// can return DIFFERENT hosts for the same affiliate. Missing
// `kqzyfj.com` here caused every prod Book Now to fail `_sanitize`,
// then fall to the `/explore` redirect on line ~119, which is
// `<Protected>` — so the fresh tab (no auth cookies) got bounced to
// `/auth`. Symptom was "book now brings me to the travelbio login".
// Reference: CJ's `linkfire` redirect domains — jdoqocy, tkqlhce,
// kqzyfj, dpbolvw, anrdoezrs, emjcd, ftjcfx.
const ALLOWED_HOSTS = [
  "www.booking.com",
  "booking.com",
  // CJ Affiliate redirect domains (all rotate — advertiser-specific)
  "www.jdoqocy.com",  "jdoqocy.com",
  "www.tkqlhce.com",  "tkqlhce.com",
  "www.kqzyfj.com",   "kqzyfj.com",
  "www.dpbolvw.net",  "dpbolvw.net",
  "www.anrdoezrs.net", "anrdoezrs.net",
  "www.emjcd.com",    "emjcd.com",
  "www.ftjcfx.com",   "ftjcfx.com",
  "www.awltovhc.com", "awltovhc.com",
  "www.qksrv.net",    "qksrv.net",
  "www.lduhtrp.net",  "lduhtrp.net",
  // Amazon outbound (Amazon Associates)
  "www.amazon.com", "amazon.com",
  "www.amazon.co.uk", "amazon.co.uk",
  "www.amazon.ca", "amazon.ca",
  "www.amazon.de", "amazon.de",
  "www.amazon.fr", "amazon.fr",
  "www.amazon.co.jp", "amazon.co.jp",
  "www.amazon.com.au", "amazon.com.au",
  // Allianz Global Assistance / AgentMax (Travel Insurance)
  "www.agentmaxonline.com", "agentmaxonline.com",
];

const DEFAULT_DELAY_MS = 800;
const MAX_DELAY_MS = 4000;

function _sanitize(raw) {
  if (!raw) return null;
  try {
    const u = new URL(raw);
    if (u.protocol !== "https:" && u.protocol !== "http:") return null;
    if (!ALLOWED_HOSTS.includes(u.host)) return null;
    return u.toString();
  } catch {
    return null;
  }
}

function _resolveDelay(params) {
  const raw = params.get("delay");
  if (!raw) return DEFAULT_DELAY_MS;
  const n = parseInt(raw, 10);
  if (Number.isNaN(n) || n < 0) return DEFAULT_DELAY_MS;
  return Math.min(n, MAX_DELAY_MS);
}

// Cache-Control via meta tags. Real HTTP header would need edge
// config on the ingress — this at least tells intermediate caches +
// the browser to skip the disk cache on this route.
function _installNoStoreMetas() {
  const tags = [
    ["cache-control", "no-store, no-cache, must-revalidate, max-age=0"],
    ["pragma", "no-cache"],
    ["expires", "0"],
  ];
  const created = [];
  for (const [name, value] of tags) {
    const el = document.createElement("meta");
    el.setAttribute("http-equiv", name);
    el.setAttribute("content", value);
    document.head.appendChild(el);
    created.push(el);
  }
  return () => created.forEach((el) => { try { el.remove(); } catch (_) {} });
}

export default function BookingTransitPage() {
  useEffect(() => {
    const removeMetas = _installNoStoreMetas();
    // Add a no-referrer meta so Booking can't correlate the request
    // with an in-browser session cached from a prior visit.
    const refEl = document.createElement("meta");
    refEl.setAttribute("name", "referrer");
    refEl.setAttribute("content", "no-referrer");
    document.head.appendChild(refEl);

    const params = new URLSearchParams(window.location.search);
    const raw = params.get("url");
    const target = _sanitize(raw);
    // 2026-07-15 rev 4 (Bug #21, 8th recurrence) — dwell bumped from
    // 1500ms to 2500ms across ALL variants. Diagnostic showed Booking's
    // own 301 redirect converts `&` → `;` in query strings and strips
    // most affiliate/label/radius params on the server side; the tab
    // then loads the coordinate-centered results, but on slow networks
    // and returning-visitor sessions there is still a perceptible
    // flash of Booking's cached homepage banner during hydration. The
    // extra 1s of dwell masks the flash and gives the target URL
    // enough time to warm before we hand off. `?delay=` still wins.
    const defaultDelay = 2500;
    const rawDelay = params.get("delay");
    const delayMs = rawDelay != null
      ? Math.min(Math.max(parseInt(rawDelay, 10) || defaultDelay, 0), MAX_DELAY_MS)
      : defaultDelay;
    if (!target) {
      window.location.replace("/explore");
      return () => { removeMetas(); try { refEl.remove(); } catch (_) {} };
    }
    const t = setTimeout(() => {
      try { window.location.replace(target); } catch { window.location.href = target; }
    }, delayMs);
    return () => {
      clearTimeout(t); removeMetas();
      try { refEl.remove(); } catch (_) {}
    };
  }, []);

  // 2026-07-17 — Booking-only disclosure. This transit page also
  // handles Amazon / Allianz outbounds so we render the note only
  // when the sanitized target host is booking.com or the CJ redirect
  // chain (which resolves to booking.com). Copy per product spec — no
  // URL-layer changes, just a helpful heads-up while the plane climbs.
  const _rawTarget = (() => {
    try { return new URLSearchParams(window.location.search).get("url"); }
    catch { return null; }
  })();
  const _isBookingPath = (() => {
    if (!_rawTarget) return false;
    try {
      const u = new URL(_rawTarget);
      const host = u.host.toLowerCase();
      if (host === "booking.com" || host === "www.booking.com") return true;
      // CJ redirect domains all resolve to Booking for the TravelBio
      // advertiser account. If the caller passed a CJ deep-link the
      // note still applies.
      const CJ_HOSTS = [
        "jdoqocy.com","tkqlhce.com","kqzyfj.com","dpbolvw.net",
        "anrdoezrs.net","emjcd.com","ftjcfx.com","awltovhc.com",
        "qksrv.net","lduhtrp.net",
      ];
      const bare = host.replace(/^www\./, "");
      return CJ_HOSTS.includes(bare);
    } catch { return false; }
  })();

  return (
    <div
      data-testid="booking-transit-page"
      role="status"
      aria-live="polite"
      style={{
        position: "fixed",
        inset: 0,
        backgroundColor: "#1a3a5f",
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {/* Plane centered vertically — nose points up-right, no clipping.
          Take-off climb still animates in from ~15% down, settles at
          center. */}
      <div
        className="tb-transit-climb"
        aria-hidden="true"
        style={{
          transform: "translateY(-4%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div className="tb-transit-bobble">
          <Plane
            size={260}
            strokeWidth={1.5}
            color="#e6b84a"
            fill="#e6b84a"
            style={{
              transform: "rotate(-45deg)",
              filter: "drop-shadow(0 18px 46px rgba(230,184,74,0.42))",
            }}
          />
        </div>
      </div>

      {/* 2026-07-17 — Booking-only Genius-member heads-up. Rendered as
          a small footer strip on the transit page so returning Booking
          members who land on their own dashboard instead of the search
          results page know why + how to still support TravelBio. */}
      {_isBookingPath && (
        <div
          data-testid="booking-transit-genius-note"
          className="tb-booking-note"
          style={{
            position: "absolute",
            left: 0, right: 0, bottom: "5vh",
            padding: "0 1.25rem",
            textAlign: "center",
            color: "rgba(255,255,255,0.78)",
            fontFamily: "'Kalam', 'Playfair Display', serif",
            fontSize: "13px",
            lineHeight: 1.5,
            maxWidth: 640,
            margin: "0 auto",
          }}
        >
          Note: Booking.com members may be redirected to their dashboard.
          If that happens, just search from there — you&rsquo;ll still support TravelBio.
        </div>
      )}

      <style>{`
        @keyframes tb-transit-bobble-kf {
          0%, 100% { transform: translateY(0); }
          50%      { transform: translateY(-8px); }
        }
        .tb-transit-bobble {
          animation: tb-transit-bobble-kf 1.6s ease-in-out infinite;
        }
        @keyframes tb-transit-climb-kf {
          0%   { transform: translateY(18%); opacity: 0.85; }
          40%  { opacity: 1; }
          100% { transform: translateY(-4%); opacity: 1; }
        }
        .tb-transit-climb {
          animation: tb-transit-climb-kf 1.8s cubic-bezier(0.22, 0.8, 0.32, 1) forwards;
        }
        @media (prefers-reduced-motion: reduce) {
          .tb-transit-bobble, .tb-transit-climb { animation: none; }
        }
      `}</style>
    </div>
  );
}
